import React from 'react';
import { Link } from 'react-router-dom';
import { Project } from '../../services/projectService';

export interface ProjectCardProps extends Omit<Project, 'revenueStatus' | 'monthlyRevenue' | 'annualRevenue'> {
  revenueStatus?: 'pre-revenue' | 'revenue' | 'na';
  monthlyRevenue?: number;
  annualRevenue?: number;
  tags?: string[];
  userCount?: number;
  ytdGrowth?: number;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  _id,
  title,
  description,
  imageUrl,
  price,
  status,
  category,
  tags,
  revenueStatus = 'na',
  monthlyRevenue,
  annualRevenue,
  userCount,
  ytdGrowth,
}) => {
  const formatRevenue = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  const getRevenueDisplay = () => {
    if (revenueStatus === 'pre-revenue') {
      return { status: 'Pre-revenue', amount: null };
    } else if (revenueStatus === 'revenue') {
      if (monthlyRevenue) {
        return { status: 'MRR', amount: formatRevenue(monthlyRevenue) };
      } else if (annualRevenue) {
        return { status: 'ARR', amount: formatRevenue(annualRevenue) };
      }
      return { status: 'Revenue', amount: null };
    }
    return { status: 'N/A', amount: null };
  };

  const revenue = getRevenueDisplay();

  return (
    <Link to={`/projects/${_id}`} className="block h-full">
      <div className="bg-white rounded-xl shadow-soft overflow-hidden transition-all duration-200 hover:shadow-medium hover:-translate-y-1 h-full flex flex-col">
        <div className="relative w-full pt-[56.25%]"> {/* 16:9 aspect ratio */}
          <img
            src={imageUrl || '/placeholder-project.png'}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Tags overlay */}
          {tags && tags.length > 0 && (
            <div className="absolute top-2 right-2 flex flex-wrap gap-1 justify-end max-w-[80%]">
              {tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs font-medium text-white bg-black/50 backdrop-blur-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="px-2 py-1 text-xs font-medium text-white bg-black/50 backdrop-blur-sm rounded-full">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-semibold text-gray-900 line-clamp-2">{title}</h3>
          </div>
          <p className="text-gray-600 mb-4 line-clamp-2 flex-1">{description}</p>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-primary-600 font-semibold">
                {typeof price === 'number' ? `$${price.toLocaleString()}` : 'No price set'}
              </div>
              {category && (
                <span className="px-3 py-1 text-sm font-medium text-primary-700 bg-primary-50 rounded-full">
                  {category}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className={`px-2 py-0.5 rounded ${
                revenueStatus === 'pre-revenue' 
                  ? 'bg-yellow-50 text-yellow-700'
                  : revenueStatus === 'revenue'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-50 text-gray-700'
              }`}>
                {revenue.status}
                {revenue.amount && ` ${revenue.amount}`}
              </span>
              {userCount && (
                <span className="px-2 py-0.5 text-blue-700 bg-blue-50 rounded">
                  {userCount.toLocaleString()} users
                </span>
              )}
              {ytdGrowth && (
                <span className={`px-2 py-0.5 rounded ${
                  ytdGrowth >= 0 
                    ? 'text-green-700 bg-green-50' 
                    : 'text-red-700 bg-red-50'
                }`}>
                  {ytdGrowth >= 0 ? '+' : ''}{ytdGrowth}% YTD
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}; 